/**
 * Where every mark on the progression chart goes. No React, no DOM.
 *
 * Pure so it can be tested as arithmetic, which is what it is, and because the
 * interesting parts -- a rounded axis top, labels that drop rather than stack
 * when lines converge, a line that starts at its driver's debut instead of at
 * zero -- are all decisions that deserve a test rather than a look.
 *
 * This and `lib/decimal.ts` are the only two modules allowed to turn a points
 * string into a number: an axis is pixels, and pixels are numbers. The
 * conversion happens here and nowhere else, which
 * `scripts/check-points-discipline.mjs` enforces.
 */

import { pointsToNumber } from '../lib/decimal'
import type { ProgressionRound, ProgressionSeries } from '../api/types'

export interface Box {
  x: number
  y: number
  width: number
  height: number
}

export interface Dot {
  x: number
  y: number
  round: number
  /** The points value as the server sent it, for labels and tooltips. */
  points: string
}

export interface Line {
  key: string
  name: string
  /**
   * Final championship rank, 1..n over the whole field.
   *
   * The colour slot comes from this and not from the series' index in the
   * selection, so pinning or unpinning a competitor never repaints the others.
   */
  rank: number
  /** 1-8 for a categorical hue, 0 for the recessive "other" treatment. */
  slot: number
  path: string
  dots: Dot[]
  /** Rounds that paid points without moving the total. */
  capped: Dot[]
  end: Dot | null
  /**
   * The end label, if this series got one.
   *
   * `anchor` is the line's own end: when the label had to move to avoid a
   * neighbour, the two differ and a leader line connects them, so the label
   * never detaches from what it names.
   */
  label: { x: number; y: number; text: string; anchor: number } | null
}

export interface Tick {
  position: number
  label: string
  /** False for a round this championship ignores. */
  counts?: boolean
}

export interface ChartGeometry {
  plot: Box
  width: number
  height: number
  lines: Line[]
  xTicks: Tick[]
  yTicks: Tick[]
  /** One x per round, for the crosshair to snap to. */
  columns: { round: number; x: number; label: string }[]
  max: number
}

export const PADDING = { top: 16, right: 92, bottom: 30, left: 44 }
/** Roughly one character of the 11px label face. Enough to reserve space. */
const CHAR_WIDTH = 6.3
const LABEL_OFFSET = 14
/** Past eight, a hue would have to be invented, so the tail goes grey. */
export const MAX_HUES = 8
/** Labels nearer than this collide, and a stacked label is worse than none. */
export const LABEL_GAP = 13
export const MAX_LABELS = 4
/** Room for one round code on the x axis: three 10px capitals and a gap. */
export const TICK_ROOM = 30

export function buildChart(
  rounds: ProgressionRound[],
  series: ProgressionSeries[],
  width: number,
  height: number,
): ChartGeometry {
  // The right gutter is measured from the names that will go in it rather
  // than fixed, because a clipped label is worse than no label and "Gerhard
  // Berger" does not fit in a guess.
  const gutter = rightGutter(series, width)
  const plot: Box = {
    x: PADDING.left,
    y: PADDING.top,
    width: Math.max(10, width - PADDING.left - gutter),
    height: Math.max(10, height - PADDING.top - PADDING.bottom),
  }

  const max = niceCeiling(peak(series))
  const x = (index: number) =>
    plot.x + (rounds.length < 2 ? plot.width / 2 : (index / (rounds.length - 1)) * plot.width)
  const y = (value: number) =>
    plot.y + plot.height - (max === 0 ? 0 : (value / max) * plot.height)

  const lines = series.map((line) => toLine(line, rounds, x, y))
  placeLabels(lines, plot)

  return {
    plot,
    width,
    height,
    lines,
    xTicks: xTicks(rounds, x, plot.width),
    yTicks: yTicks(max, y),
    columns: rounds.map((round, index) => ({
      round: round.round,
      x: x(index),
      label: round.code ?? String(round.round),
    })),
    max,
  }
}

/**
 * How much room to leave for the end labels.
 *
 * Only the series that can get one are measured -- the best four -- and the
 * result is capped at a third of the chart, so a long name narrows the plot
 * rather than swallowing it.
 */
export function rightGutter(series: ProgressionSeries[], width: number): number {
  const longest = [...series]
    .sort((a, b) => a.rank - b.rank)
    .slice(0, MAX_LABELS)
    .reduce((most, line) => Math.max(most, (line.name ?? line.entity).length), 0)
  const wanted = longest * CHAR_WIDTH + LABEL_OFFSET + 6
  return Math.min(Math.max(PADDING.right, wanted), Math.max(PADDING.right, width * 0.34))
}

/** The highest total any series reaches. */
export function peak(series: ProgressionSeries[]): number {
  let top = 0
  for (const line of series) {
    for (const value of line.points) {
      if (value === null) continue
      const number = pointsToNumber(value)
      if (number > top) top = number
    }
  }
  return top
}

/**
 * An axis top a reader can divide: 10, 25, 50, 100, 150 and so on.
 *
 * Never below the data, and never zero, so an axis always has a scale even
 * before anybody has scored.
 */
export function niceCeiling(value: number): number {
  if (value <= 0) return 10
  const magnitude = 10 ** Math.floor(Math.log10(value))
  for (const step of [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]) {
    const candidate = step * magnitude
    if (candidate >= value) return round2(candidate)
  }
  return round2(10 * magnitude)
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}

function toLine(
  line: ProgressionSeries,
  rounds: ProgressionRound[],
  x: (index: number) => number,
  y: (value: number) => number,
): Line {
  const dots: Dot[] = []
  const capped: Dot[] = []
  const cappedRounds = new Set(line.capped_rounds ?? [])

  rounds.forEach((round, index) => {
    const value = line.points[index]
    // Null means "had not entered yet", so the line starts at the driver's
    // debut rather than crawling along the axis from round one.
    if (value === null || value === undefined) return
    const dot: Dot = {
      x: x(index),
      y: y(pointsToNumber(value)),
      round: round.round,
      points: value,
    }
    dots.push(dot)
    if (cappedRounds.has(round.round)) capped.push(dot)
  })

  return {
    key: line.key,
    name: line.name ?? line.entity,
    rank: line.rank,
    slot: line.rank <= MAX_HUES ? line.rank : 0,
    path: path(dots),
    dots,
    capped,
    end: dots.at(-1) ?? null,
    label: null,
  }
}

/** A polyline, written as a path so one attribute carries the whole series. */
export function path(dots: Dot[]): string {
  if (dots.length === 0) return ''
  if (dots.length === 1) {
    const only = dots[0]!
    // A single point has no line, so it gets a one-pixel stub to be visible.
    return `M${round1(only.x)} ${round1(only.y)}h0.01`
  }
  return dots
    .map((dot, index) => `${index === 0 ? 'M' : 'L'}${round1(dot.x)} ${round1(dot.y)}`)
    .join(' ')
}

function round1(value: number): number {
  return Math.round(value * 10) / 10
}

/**
 * End labels for the best-placed series, nudged apart and tethered.
 *
 * 1988 is the case that decides the design: Senna ends on 90 and Prost on 87,
 * nine pixels apart, so one of them has to move. Dropping Prost's label loses
 * the one comparison the chart exists to make, and shifting it without a
 * connector detaches it from its line -- so the label moves and a leader line
 * follows it. Four at most, because past that they are noise even when they
 * fit, and the legend carries the rest.
 */
export function placeLabels(lines: Line[], plot?: Box): void {
  const chosen = [...lines]
    .filter((line) => line.end !== null)
    .sort((a, b) => a.rank - b.rank)
    .slice(0, MAX_LABELS)
    .sort((a, b) => a.end!.y - b.end!.y)

  if (chosen.length === 0) return

  const positions = chosen.map((line) => line.end!.y)
  // Downward pass: push each label clear of the one above it.
  for (let index = 1; index < positions.length; index += 1) {
    const above = positions[index - 1]!
    if (positions[index]! - above < LABEL_GAP) positions[index] = above + LABEL_GAP
  }
  // The group may now hang below the plot, so pull it back and re-separate
  // upwards. Both passes together keep the order of the series intact.
  if (plot !== undefined) {
    const bottom = plot.y + plot.height
    const overflow = positions.at(-1)! - bottom
    if (overflow > 0) {
      for (let index = 0; index < positions.length; index += 1) {
        positions[index] = positions[index]! - overflow
      }
      for (let index = positions.length - 2; index >= 0; index -= 1) {
        const below = positions[index + 1]!
        if (below - positions[index]! < LABEL_GAP) positions[index] = below - LABEL_GAP
      }
    }
  }

  chosen.forEach((line, index) => {
    const end = line.end!
    line.label = {
      x: end.x + LABEL_OFFSET,
      y: positions[index]!,
      text: line.name,
      anchor: end.y,
    }
  })
}

/**
 * Round labels, thinned so they never overlap.
 *
 * A twenty-four round season cannot show twenty-four three-letter codes in the
 * width available, so labels are dropped at a steady interval until each has
 * about `TICK_ROOM` pixels -- but the first and last always survive, because
 * they anchor the axis, and the regular tick before the last gives way to it.
 */
export function xTicks(
  rounds: ProgressionRound[],
  x: (index: number) => number,
  width = Infinity,
): Tick[] {
  const fits = Math.max(2, Math.min(12, Math.floor(width / TICK_ROOM)))
  const every = Math.max(1, Math.ceil(rounds.length / fits))
  const last = rounds.length - 1
  return rounds
    .map((round, index) => ({ round, index }))
    .filter(({ index }) => index === last || (index % every === 0 && last - index >= every))
    .map(({ round, index }) => ({
      position: x(index),
      label: round.code ?? String(round.round),
      counts: round.counts,
    }))
}

/** Five gridlines on clean numbers, including zero and the top. */
export function yTicks(max: number, y: (value: number) => number): Tick[] {
  const steps = 4
  return Array.from({ length: steps + 1 }, (_, index) => {
    const value = (max / steps) * index
    return { position: y(value), label: format(value) }
  })
}

function format(value: number): string {
  return Number.isInteger(value) ? String(value) : String(round2(value))
}
